(function (global) {
  'use strict';

  var routePromises = Object.create(null);
  var scriptPromises = Object.create(null);
  var preloadUrls = Object.create(null);

  function routesFor(node) {
    return String(node && node.dataset && node.dataset.routes || '').split(/\s+/).filter(Boolean);
  }

  // Load the active locale and its fallback catalogs. Without i18n, load all.
  function localeNeeded(node) {
    var code = node && node.dataset && node.dataset.locale;
    if (!code) return true;
    var i18n = global.PlummeI18n;
    if (!i18n || typeof i18n.neededLocales !== 'function') return true;
    return i18n.neededLocales().indexOf(code) !== -1;
  }

  function nodesFor(route, selector) {
    return Array.from(document.querySelectorAll(selector)).filter(function (node) {
      return routesFor(node).includes(route) && localeNeeded(node);
    });
  }

  function productionScriptUrl(source) {
    return global.Babel ? source : source.replace(/\.(?:jsx|tsx|ts)(?=\?|$)/, '.js');
  }

  function preloadProductionScript(node) {
    if (global.Babel) return;
    var source = node.getAttribute('src');
    if (!source) return;
    var url = productionScriptUrl(source);
    if (preloadUrls[url]) return;
    preloadUrls[url] = true;
    var preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'script';
    preload.href = url;
    preload.dataset.plummeRoutePreload = 'true';
    document.head.appendChild(preload);
  }

  function executeDevelopmentSource(node, source) {
    return fetch(source).then(function (response) {
      if (!response.ok) throw new Error('Kunne ikke laste ' + source + ' (HTTP ' + response.status + ')');
      return response.text();
    }).then(function (code) {
      var compiled = global.Babel.transform(code, { presets: ['react'], sourceFileName: source }).code;
      var script = document.createElement('script');
      script.text = compiled + '\n//# sourceURL=' + source.split('?')[0];
      document.body.appendChild(script);
      node.dataset.loaded = 'true';
    });
  }

  function executeScript(node) {
    var source = node.getAttribute('src');
    if (!source || node.dataset.loaded === 'true') return Promise.resolve();
    if (scriptPromises[source]) return scriptPromises[source];
    // Vite strips TypeScript in development; Babel handles any remaining JSX.
    var isSourceScript = /\.(?:jsx|tsx|ts)(?:\?|$)/.test(source);
    var promise = global.Babel && isSourceScript
      ? executeDevelopmentSource(node, source)
      : new Promise(function (resolve, reject) {
          var script = document.createElement('script');
          script.src = productionScriptUrl(source);
          script.onload = function () { node.dataset.loaded = 'true'; resolve(); };
          script.onerror = function () { reject(new Error('Kunne ikke laste ' + source)); };
          document.body.appendChild(script);
        });
    scriptPromises[source] = promise.catch(function (error) {
      node.dataset.loadError = 'true';
      delete scriptPromises[source];
      throw error;
    });
    return scriptPromises[source];
  }

  function activateStyles(route) {
    return Promise.all(nodesFor(route, 'link[rel="plumme-lazy-stylesheet"][data-routes]').map(function (link) {
      if (link.dataset.loaded === 'true') return Promise.resolve();
      return new Promise(function (resolve) {
        link.addEventListener('load', resolve, { once: true });
        // En stilfeil skal ikke gjore en ellers fungerende side utilgjengelig.
        link.addEventListener('error', resolve, { once: true });
        // Sett relasjonen forst. Enkelte nettlesere starter ikke en stylesheet-
        // foresporsel nar href settes mens rel fortsatt er en ukjent verdi.
        link.rel = 'stylesheet';
        link.href = link.dataset.href;
        link.dataset.loaded = 'true';
        if (link.sheet) resolve();
      });
    }));
  }

  // `codeOnly` ignores pending language catalogs. A page that is already on
  // screen keeps rendering while they load; `t()` falls back until they land.
  // Swapping it for a loader would remount it and discard unsaved form state.
  function hasRoute(route, options) {
    var codeOnly = !!(options && options.codeOnly);
    return nodesFor(route, 'script[type="application/plumme-lazy"][data-routes]')
      .some(function (node) {
        if (codeOnly && node.dataset.locale) return false;
        return node.dataset.loaded !== 'true' && node.dataset.loadError !== 'true';
      })
      // Shared scripts may already be loaded by another route while this
      // route still needs its own stylesheet before rendering.
      || nodesFor(route, 'link[rel="plumme-lazy-stylesheet"][data-routes]').length > 0;
  }

  function loadRoute(route) {
    if (!route) return Promise.resolve();
    if (routePromises[route]) return routePromises[route];
    nodesFor(route, 'script[type="application/plumme-lazy"][data-routes]').forEach(function (node) { delete node.dataset.loadError; });
    if (!hasRoute(route)) return Promise.resolve();
    var scripts = nodesFor(route, 'script[type="application/plumme-lazy"][data-routes]');
    // Start stilarket forst og alle produksjonsskriptene rett etterpa. Preloadene
    // hentes parallelt, mens reduce-kjeden under fortsatt evaluerer klassiske
    // skript i indeksrekkefolge slik at globale avhengigheter bevares.
    var stylesReady = activateStyles(route);
    scripts.forEach(preloadProductionScript);
    routePromises[route] = stylesReady.then(function () {
      return scripts.reduce(function (chain, node) {
        return chain.then(function () { return executeScript(node); });
      }, Promise.resolve());
    }).then(function () {
      global.dispatchEvent(new CustomEvent('plumme-page-registered', { detail: { route: route } }));
      global.PlummeStartupPerformance && global.PlummeStartupPerformance.mark('route-ready:' + route);
    }).catch(function (error) {
      delete routePromises[route];
      console.error('[plumme-page-loader] Ruten kunne ikke lastes:', route, error);
      global.dispatchEvent(new CustomEvent('plumme-page-load-error', { detail: { route: route } }));
      throw error;
    });
    return routePromises[route];
  }

  // Kataloger som ikke hoerer til en enkelt rute. De var tidligere vanlige
  // <script src>, altsaa synkront lastet paa hver sidevisning uansett spraak.
  // Naa henter vi bare den katalogen brukeren faktisk slaar opp i.
  function globalLocaleNodes() {
    return Array.from(document.querySelectorAll('script[type="application/plumme-locale"][data-locale]'));
  }

  // Lastes ved oppstart, og paa nytt naar brukeren bytter spraak.
  //
  // Rutekatalogene ligger allerede som plumme-lazy-tagger og filtreres av
  // `nodesFor`. De er derfor ikke lastet for et spraak som ikke var aktivt da
  // ruten ble aapnet — derfor nullstiller vi `routePromises` og kjoerer rutene
  // paa nytt. `executeScript` deduperer paa `src` og setter `data-loaded`, saa
  // et nytt gjennomloep henter bare filene som mangler.
  function loadLocale(code) {
    var wanted = String(code || '');
    if (!wanted) return Promise.resolve();

    var needed = global.PlummeI18n && typeof global.PlummeI18n.neededLocales === 'function'
      ? global.PlummeI18n.neededLocales() : [wanted];
    var pending = globalLocaleNodes()
      .filter(function (node) { return needed.indexOf(node.dataset.locale) !== -1 && node.dataset.loaded !== 'true'; })
      .reduce(function (chain, node) {
        return chain.then(function () { return executeScript(node); });
      }, Promise.resolve());

    var loadedRoutes = Object.keys(routePromises);
    return pending.then(function () {
      loadedRoutes.forEach(function (route) { delete routePromises[route]; });
      return Promise.all(loadedRoutes.map(function (route) { return loadRoute(route); }));
    }).then(function () {
      global.dispatchEvent(new CustomEvent('plumme-locale-loaded', { detail: { locale: wanted } }));
    }).catch(function (error) {
      console.error('[plumme-page-loader] Spraakkatalogen kunne ikke lastes:', wanted, error);
    });
  }

  global.PlummePageLoader = Object.freeze({
    hasRoute: hasRoute,
    loadRoute: loadRoute,
    loadLocale: loadLocale,
  });
})(window);
